/**
 * Sidebar component with collapsible nested navigation.
 */

import { useLocation, useNavigate } from 'react-router-dom'
import {
  Activity,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  FolderOpen,
  Database,
  ShieldAlert,
  TrendingUp,
  Users,
  AlertTriangle,
  AlertCircle,
  GitCompare,
  GitBranch,
  Settings,
  Shield,
  FolderCog,
  FileText,
  Cog,
  Blocks,
  Upload,
  Lock,
  List,
  User,
  StickyNote,
  MessageSquare,
  Tags,
  BookOpen,
  Palette,
  Workflow,
  Package,
  Terminal,
  Code2,
  Dna,
  FileSpreadsheet,
  FileCheck,
  Brain,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useNavigationStore, defaultNavigation, NavigationItem } from '@/stores/navigationStore'
import { useEffect, useState } from 'react'
import { fetchCurrentVersion } from '@/lib/versionApi'
import { useStudyStore } from '@/stores/studyStore'
import { useAuthContext } from '@/hooks/AuthContext'
import { useThemeContext } from '@/contexts/ThemeContext'
import { useBrandingStore } from '@/stores/brandingStore'
import { cn } from '@/lib/utils'
import { isOnStudySubdomain, navigateToMainApp } from '@/lib/subdomain'

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  Activity,
  Bell,
  LayoutDashboard,
  FolderOpen,
  Database,
  ShieldAlert,
  TrendingUp,
  Users,
  AlertTriangle,
  AlertCircle,
  GitCompare,
  GitBranch,
  Settings,
  Shield,
  FolderCog,
  FileText,
  Cog,
  Blocks,
  Upload,
  Lock,
  List,
  User,
  StickyNote,
  MessageSquare,
  Tags,
  BookOpen,
  Palette,
  Workflow,
  Package,
  Terminal,
  Code2,
  Dna,
  FileSpreadsheet,
  FileCheck,
  Brain,
}

interface SectionLabelProps {
  item: NavigationItem
  isCollapsed: boolean
}

function SectionLabel({ item, isCollapsed }: SectionLabelProps) {
  const color = item.color || '#3B82F6'

  if (isCollapsed) {
    // Collapsed: thin colored line
    return (
      <div
        className="-mx-2 my-2"
        style={{ backgroundColor: color, height: '2px' }}
      />
    )
  }

  // Expanded: full-bleed colored band with uppercase white text
  return (
    <div
      className="-mx-2 px-3 py-2"
      style={{ backgroundColor: color }}
    >
      <span className="text-[0.8rem] font-semibold tracking-wider text-white uppercase">
        {item.label}
      </span>
    </div>
  )
}

interface NavItemProps {
  item: NavigationItem
  isCollapsed: boolean
  level?: number
  isAddonFeature?: boolean // Skip page access check for addon feature items
}

function NavItem({ item, isCollapsed, level = 0, isAddonFeature = false }: NavItemProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasRole, isLoading: isAuthLoading } = useAuthContext()
  const currentStudy = useStudyStore((state) => state.currentStudy)
  const { expandedItems, toggleExpandedItem, setSidebarOpen, isPageAccessible, hasPageRestrictions } = useNavigationStore()

  // If this item or its parent has requiredFeature, it's an addon feature
  const isAddon = isAddonFeature || !!item.requiredFeature

  const Icon = item.icon ? iconMap[item.icon] : null
  const hasChildren = item.children && item.children.length > 0
  const isExpanded = !!expandedItems[item.id]
  const onSubdomain = isOnStudySubdomain()

  // Check if user has required roles (for hardcoded role-based items like admin)
  // Don't hide items while auth is still loading
  if (item.requiredRoles && !isAuthLoading && !item.requiredRoles.some((role) => hasRole(role))) {
    return null
  }

  // Check page access from database (if restrictions are configured)
  // Extract slug from path (e.g., "/study/:studyId/safety/ae-overview" -> "ae-overview")
  // For items without children, check their slug
  // IMPORTANT: Admin routes (/admin/*) are NOT subject to page access restrictions
  // IMPORTANT: Addon feature items bypass page access checks (they're not in DB)
  // IMPORTANT: Study-specific pages (id starts with "study-") bypass page access checks
  //            because they come from study_pages table, not navigation_items template
  const isStudySpecificPage = item.id.startsWith('study-')
  if (hasPageRestrictions && !hasChildren && item.path && !item.path.startsWith('/admin') && !isAddon && !isStudySpecificPage) {
    // Extract the last segment of the path as the slug
    const pathParts = item.path.split('/').filter(Boolean)
    const slug = pathParts[pathParts.length - 1]
    // Skip study ID placeholders
    if (slug && slug !== ':studyId' && !isPageAccessible(slug)) {
      return null
    }
  }

  // Check if item requires study and no study is selected (only on main domain)
  const requiresStudy = item.requiresStudy && !currentStudy && !onSubdomain

  // Build the actual path
  let actualPath: string | undefined
  if (item.path) {
    if (onSubdomain) {
      // On subdomain: strip /study/:studyId prefix for study-scoped paths
      actualPath = item.path.replace(/^\/study\/:studyId/, '') || '/'
    } else {
      // On main domain: replace :studyId with actual study ID
      actualPath = item.path.replace(':studyId', currentStudy?.studyId || '')
    }
  }

  // Check if current path matches
  // For items with children (menus), use startsWith to highlight when on child pages
  // For leaf items (no children), only use exact match to avoid false positives
  const isActive = actualPath
    ? hasChildren
      ? location.pathname.startsWith(actualPath)
      : location.pathname === actualPath
    : false

  const handleClick = () => {
    if (hasChildren) {
      toggleExpandedItem(item.id)
    } else if (actualPath && !requiresStudy) {
      // When on subdomain, admin routes and studies link should go to main app
      if (onSubdomain && (item.path?.startsWith('/admin') || item.id === 'studies')) {
        navigateToMainApp(item.path || '/studies')
      } else {
        navigate(actualPath)
      }
      // Close mobile sidebar
      setSidebarOpen(false)
    }
  }

  const content = (
    <button
      onClick={handleClick}
      disabled={requiresStudy}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        requiresStudy && 'opacity-50 cursor-not-allowed',
        level > 0 && 'ml-4'
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      {!isCollapsed && (
        <>
          <span className="flex-1 text-left truncate">{item.label}</span>
          {item.badge !== undefined && item.badge !== 0 && (
            <Badge variant="secondary" className="ml-auto">
              {item.badge}
            </Badge>
          )}
          {hasChildren && (
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 transition-transform',
                isExpanded && 'rotate-180'
              )}
            />
          )}
        </>
      )}
    </button>
  )

  if (isCollapsed && !hasChildren) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right">
          {item.label}
          {requiresStudy && ' (Select a study first)'}
        </TooltipContent>
      </Tooltip>
    )
  }

  if (hasChildren) {
    return (
      <Collapsible open={isExpanded && !isCollapsed}>
        <CollapsibleTrigger asChild>{content}</CollapsibleTrigger>
        <CollapsibleContent className="pl-4 space-y-1">
          {item.children!.map((child) => (
            <NavItem
              key={child.id}
              item={child}
              isCollapsed={isCollapsed}
              level={level + 1}
              isAddonFeature={isAddon}
            />
          ))}
        </CollapsibleContent>
      </Collapsible>
    )
  }

  return content
}

function SidebarContent({ isCollapsed }: { isCollapsed: boolean }) {
  const { branding } = useBrandingStore()
  const location = useLocation()
  const {
    toggleSidebar,
    studyNavigation,
    isLoadingStudyNav,
    loadStudyNavigation,
    loadAccessiblePages,
    isLoadingAccessiblePages,
  } = useNavigationStore()
  const currentStudy = useStudyStore((state) => state.currentStudy)
  const { resolvedTheme } = useThemeContext()
  const { isAuthenticated } = useAuthContext()
  const onSubdomain = isOnStudySubdomain()

  // Platform version state
  const [platformVersion, setPlatformVersion] = useState<string | null>(null)

  // Fetch platform version on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentVersion()
        .then((info) => setPlatformVersion(info.version))
        .catch(() => setPlatformVersion(null))
    }
  }, [isAuthenticated])

  // Determine which logo to show based on resolved theme (handles 'system' preference)
  // Light theme = light background = use "Light BG" logo (dark-colored logo)
  // Dark theme = dark background = use "Dark BG" logo (light-colored logo)
  const logoSrc = resolvedTheme === 'dark' ? branding.logos.dark : branding.logos.light

  // Check if we're on a study-specific route (not just having a study selected)
  const isOnStudyRoute = location.pathname.startsWith('/study/')

  // Load accessible pages when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadAccessiblePages()
    }
  }, [isAuthenticated, loadAccessiblePages])

  // Load study navigation when study changes
  useEffect(() => {
    if (currentStudy?.studyId) {
      loadStudyNavigation(currentStudy.studyId)
    }
  }, [currentStudy?.studyId, loadStudyNavigation])

  // Determine which navigation to display
  // - On study subdomain or /study/* routes: show study navigation from database
  // - On main routes (/dashboard, /studies, /admin): show defaultNavigation
  // Backend already filters out pages requiring disabled features via required_feature.
  const getNavigationItems = (): NavigationItem[] => {
    if (onSubdomain || isOnStudyRoute) {
      return studyNavigation.length > 0 ? studyNavigation : []
    }

    // On main routes: show defaultNavigation (Dashboard, Studies, Admin groups)
    // Admin groups have requiredRoles: ['system_admin'] so non-admins only see Studies
    return defaultNavigation
  }

  const navigationItems = getNavigationItems()

  return (
    <div className="flex h-full flex-col">
      {/* Logo and Version */}
      <div className="border-b px-4 py-3">
        <div className={cn('flex flex-col', isCollapsed && 'items-center')}>
          {isCollapsed ? (
            branding.logos.icon ? (
              <img src={branding.logos.icon} alt={branding.appName} className="h-8 w-8" />
            ) : (
              <span className="font-bold text-lg">{branding.appName[0]}</span>
            )
          ) : (
            <>
              {logoSrc ? (
                <img src={logoSrc} alt={branding.appName} className="h-8" />
              ) : (
                <span className="font-bold text-xl">{branding.appName}</span>
              )}
              {platformVersion && (
                <span className="text-xs text-muted-foreground mt-1 self-end">v{platformVersion}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Collapse toggle - moved to top */}
      <div className="hidden lg:flex border-b px-2 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={toggleSidebar}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4 mr-2" />
              <span>Collapse sidebar</span>
            </>
          )}
        </Button>
      </div>

      {/* Current study indicator - only show on study routes or subdomain */}
      {currentStudy && !isCollapsed && (onSubdomain || isOnStudyRoute) && (
        <div className="border-b px-4 py-3">
          <p className="text-xs text-muted-foreground">Current Study</p>
          <p className="font-medium truncate">{currentStudy.name}</p>
          <p className="text-xs text-muted-foreground">{currentStudy.studyId}</p>
        </div>
      )}

      {/* Navigation */}
      <ScrollArea className="flex-1 px-2 py-4">
        {isLoadingStudyNav || isLoadingAccessiblePages ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        ) : navigationItems.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            <p>No navigation configured.</p>
            <p className="mt-1">Contact your administrator.</p>
          </div>
        ) : (
          <nav className="space-y-1">
            {navigationItems.map((item) =>
              item.navType === 'section' ? (
                <SectionLabel key={item.id} item={item} isCollapsed={isCollapsed} />
              ) : (
                <NavItem key={item.id} item={item} isCollapsed={isCollapsed} />
              )
            )}
          </nav>
        )}
      </ScrollArea>

      {/* Footer */}
      {!isCollapsed && (
        <div className="border-t p-4 space-y-3">
          {/* User Guide Link - only show on main app, not study subdomains */}
          {!onSubdomain && (
            <button
              onClick={() => window.open('/admin/user-guide', '_blank')}
              className="flex items-center justify-center gap-2 w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <BookOpen className="h-3.5 w-3.5" />
              <span>User Guide</span>
            </button>
          )}

          {/* Powered by */}
          {branding.footer.showPoweredBy && (
            <p className="text-xs text-muted-foreground text-center">
              {branding.footer.poweredByText}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export function Sidebar() {
  const { isSidebarCollapsed, isSidebarOpen, setSidebarOpen } = useNavigationStore()

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 hidden border-r bg-background lg:flex flex-col transition-all duration-300',
          isSidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <SidebarContent isCollapsed={isSidebarCollapsed} />
      </aside>

      {/* Mobile sidebar (sheet) */}
      <Sheet open={isSidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-64 p-0" aria-describedby={undefined}>
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SidebarContent isCollapsed={false} />
        </SheetContent>
      </Sheet>
    </>
  )
}
